import {
  DcuplFiltersOptions,
  DcuplItemOptions,
  DcuplItemsOptions,
  DcuplGroupByOptions,
  FilterItem,
  ListItem,
  ListMetadata,
  GroupByResponse,
  trigger,
  ViewDefinition,
  ModelAttribute,
  DcuplFacetOptions,
  DcuplGlobalQueryOptions,
  DcuplQueryGroup,
  DcuplQuery,
  DcuplFilterOptions,
  ChangeDetector,
  DcuplListQueryOptions,
  AggregationOptions,
  Aggregation,
  DcuplManyItemsOptions,
  SuggestionOptions,
  Suggestion,
  computeSuggestions,
  PivotOptions,
  PivotResponse,
  pivot,
  IndicesController,
  DcuplFacet,
  DcuplQueryBuilder,
} from '@dcupl/common';

import { DcuplModel } from '../model-parser';
import { Filter } from './filter/filter';
import { ItemService } from './item/item';
import { DcuplApi } from '../dcupl/dcupl-api';

export class CatalogApi<ListType extends ListItem = ListItem> {
  protected metadata!: ListMetadata;
  protected model!: DcuplModel<ListType>;
  protected filter!: Filter<ListType>;
  private itemService!: ItemService<ListType>;

  constructor(
    protected dcuplApi: DcuplApi,
    protected cdRef: ChangeDetector,
    protected indicesController: IndicesController,
  ) {
    this.itemService = new ItemService<ListType & ListItem>(
      this.dcuplApi['modelParser'],
      this.dcuplApi['scriptController'],
    );
    this.filter = new Filter<ListType>(
      this.dcuplApi['modelParser'],
      this.cdRef,
      this.itemService,
      this.indicesController,
      this.dcuplApi['queryManager'],
    );
  }

  protected initListeners(): void {
    this.initListMetadata();
  }

  private initListMetadata(): void {
    this.metadata = {
      key: this.model.key,
      currentSize: this.filter.filteredData.size,
      initialSize: this.filter.initialData.size,
      attributes: this.getModelAttributes(),
      views: this.getViews(),
      appliedQuery: this.filter.queryBuilder.getQuery(),
    };
  }

  private getModelAttributes(): ModelAttribute[] {
    const attributes: ModelAttribute[] = [...this.model.references.values(), ...this.model.properties.values()];
    return attributes;
  }

  public query = {
    apply: (
      query: DcuplQueryGroup | DcuplQuery | DcuplQuery[] | DcuplQueryGroup[],
      options?: { mode?: 'set' | 'add'; skipProcessing?: boolean },
    ): DcuplGlobalQueryOptions<any> => {
      return this.applyQuery(query, options);
    },
    applyOptions: (
      itemOptions: DcuplItemsOptions<ListType>,
      options?: { mode?: 'set' | 'update'; skipProcessing?: boolean },
    ): DcuplGlobalQueryOptions<any> => {
      return this.applyOptions(itemOptions, options);
    },
    remove: (options?: Partial<DcuplQueryGroup> | DcuplQuery): DcuplGlobalQueryOptions<any> => {
      return this.removeQuery(options);
    },
    reset: (
      options: { skipProcessing?: boolean } = {
        skipProcessing: false,
      },
    ): DcuplGlobalQueryOptions<any> => {
      return this.resetQuery(options);
    },
    execute: <ItemType extends ListType = ListType>(query?: DcuplListQueryOptions<ItemType>): ItemType[] => {
      return this.executeQuery(query);
    },
    one: (options: DcuplItemOptions<ListType>): ListType | undefined => {
      return this._getItem(options);
    },
    many: (options: DcuplManyItemsOptions<ListType>): ListType[] => {
      return this._getManyItems(options);
    },
    get: (): DcuplGlobalQueryOptions<any> => {
      return this.getQuery();
    },
    has: (options: { groupKey: string; queryKey?: string }): boolean => {
      return this.hasGroupOrQueryKey(options);
    },
  };

  public filters = {
    get: (options: DcuplFilterOptions & { filterKey: string }): FilterItem | undefined => {
      return this.getFilter(options);
    },
    getAll: (options?: DcuplFiltersOptions): FilterItem[] => {
      return this.getFilters(options);
    },
  };

  public fn = {
    pivot: (options: PivotOptions): PivotResponse => {
      return this._getPivot(options);
    },
    aggregate: (options: AggregationOptions): Aggregation => {
      return this.getAggregate(options);
    },
    suggest: (options: SuggestionOptions): Suggestion[] => {
      return this._getSuggestions(options);
    },
    groupBy: (options: DcuplGroupByOptions<ListType>): GroupByResponse => {
      return this._getSections(options);
    },
    facets: (options: DcuplFacetOptions): DcuplFacet[] => {
      return this._getFacets(options);
    },
    metadata: (): ListMetadata => {
      return this._getMetadata();
    },
  };

  private getViews(): ViewDefinition[] {
    const views: ViewDefinition[] = [];
    this.filter.modelParser.views.forEach((view) => {
      if (view.model === this.model.key) {
        views.push(view);
      }
    });
    return views;
  }

  @trigger({
    scope: 'list',
    type: 'fn_metadata',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getMetadata(): ListMetadata {
    this.initListMetadata();
    return this.metadata;
  }

  private getFilters(options?: DcuplFiltersOptions): FilterItem[] {
    return this._getFilters(options);
  }

  @trigger({
    scope: 'list',
    type: 'filter_many',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getFilters(options?: DcuplFiltersOptions): FilterItem[] {
    const result = this.filter.getFilterItems(options);

    if (options?.filterKeys) {
      return options.filterKeys
        .map((key) => {
          const filter = result.find((filter) => filter.key === key);
          if (!filter) {
            console.log(`Filter ${key} not found in model ${this.model.key}`);
          }
          return filter;
        })
        .filter(Boolean) as FilterItem[];
    }

    return result;
  }

  private getFilter(options: DcuplFilterOptions & { filterKey: string }): FilterItem | undefined {
    return this._getFilter(options);
  }

  @trigger({
    scope: 'list',
    type: 'filter_one',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getFilter(options: DcuplFilterOptions & { filterKey: string }): FilterItem | undefined {
    const result = this.filter.getFilterItem(options.filterKey, options);
    return result;
  }

  @trigger({
    scope: 'list',
    type: 'query_get',
    cloneResult: true,
  })
  private getQuery(): DcuplGlobalQueryOptions<ListType> {
    return this.filter.queryBuilder.getQuery();
  }

  @trigger({
    scope: 'list',
    type: 'query_has',
  })
  private hasGroupOrQueryKey(options: { groupKey: string; queryKey?: string }): boolean {
    return this.filter.queryBuilder.has(options);
  }

  @trigger({
    scope: 'list',
    type: 'query_apply',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private applyQuery(
    queryToApply: DcuplQueryGroup | DcuplQuery | DcuplQuery[] | DcuplQueryGroup[],
    options?: { mode?: 'set' | 'add'; skipProcessing?: boolean },
  ): DcuplGlobalQueryOptions<any> {
    const query = this.filter.queryBuilder.applyQuery(queryToApply, options);

    this.filter.updateFilteredData({ skipProcessing: options?.skipProcessing });

    return query;
  }

  @trigger({
    scope: 'list',
    type: 'query_apply_options',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private applyOptions(
    itemOptions: DcuplItemsOptions<ListType>,
    options?: { mode?: 'set' | 'update'; skipProcessing?: boolean },
  ): DcuplGlobalQueryOptions<any> {
    const query = this.filter.queryBuilder.applyOptions(itemOptions, options?.mode);

    this.filter.updateFilteredData({ skipProcessing: options?.skipProcessing });

    return query;
  }

  @trigger({
    scope: 'list',
    type: 'query_remove',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private removeQuery(
    queryToRemove?: Partial<DcuplQueryGroup> | DcuplQuery,
    options?: { skipProcessing?: boolean },
  ): DcuplGlobalQueryOptions<any> {
    let query: DcuplGlobalQueryOptions<any>;
    if (queryToRemove) {
      query = this.filter.queryBuilder.removeQuery(queryToRemove);
    } else {
      query = this.filter.queryBuilder.removeAllQueries();
    }

    this.filter.updateFilteredData({ skipProcessing: options?.skipProcessing });

    return query;
  }

  @trigger({
    scope: 'list',
    type: 'query_reset',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private resetQuery(options?: { skipProcessing?: boolean }): DcuplGlobalQueryOptions<any> {
    const query = this.filter.queryBuilder.reset();

    this.filter.updateFilteredData({ skipProcessing: options?.skipProcessing });

    return query;
  }

  @trigger({
    scope: 'list',
    type: 'query_execute',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private executeQuery<ItemType extends ListType = ListType>(options?: DcuplListQueryOptions<ItemType>): ItemType[] {
    const cacheContext = this.filter.cacheCtrl.generateContext({
      options,
    });
    const hasCache = this.filter.cacheCtrl.has('pivot', cacheContext);
    if (hasCache) {
      return this.filter.cacheCtrl.get('pivot', cacheContext);
    }

    if (!options) {
      options = this.filter.queryBuilder.getQuery();
    }

    const fullOptions: DcuplGlobalQueryOptions<ItemType> = Object.assign({}, options, { modelKey: this.model.key });

    const queriedData = this.dcuplApi['queryManager'].queryData(
      this.filter.filteredData,
      fullOptions,
      false,
      this.filter.indicesController['indexMap'],
    );

    const items = this.itemService.getItems(queriedData, this.model, options as DcuplItemsOptions<ListType>);
    this.filter.cacheCtrl.set('query', cacheContext, items);
    return items as ItemType[];
  }

  @trigger({
    scope: 'list',
    type: 'fn_groupBy',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getSections(options: DcuplGroupByOptions<any>): GroupByResponse {
    const cacheContext = this.filter.cacheCtrl.generateContext({
      options,
      query: this.filter.queryBuilder.getQuery(),
    });
    const hasCache = this.filter.cacheCtrl.has('sections', cacheContext);
    if (hasCache) {
      return this.filter.cacheCtrl.get('sections', cacheContext);
    }

    const response = this.filter.getSections(options);

    this.filter.cacheCtrl.set('sections', cacheContext, response);

    return response;
  }

  @trigger({
    scope: 'list',
    type: 'query_one',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getItem<ItemType = ListType>(options: DcuplItemOptions<ItemType>): ItemType | undefined {
    const item = this.itemService.getItem(
      options.itemKey,
      this.filter.filteredData,
      this.model,
      options as DcuplItemOptions<ListType>,
    );
    return item as any;
  }

  @trigger({
    scope: 'list',
    type: 'query_many',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getManyItems<ItemType = ListType>(options: DcuplManyItemsOptions<ItemType>): ItemType[] {
    const items = this.itemService.getManyItems(options.itemKeys, this.filter.filteredData, this.model, options);
    return items as any;
  }

  @trigger({
    scope: 'list',
    type: 'fn_suggest',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getSuggestions(options: SuggestionOptions): Suggestion[] {
    // set defaults
    if (typeof options.excludeUndefineds === 'undefined') {
      options.excludeUndefineds = true;
    }
    if (typeof options.excludeNulls === 'undefined') {
      options.excludeNulls = true;
    }

    // check cache
    const cacheContext = this.filter.cacheCtrl.generateContext({
      options,
      query: this.filter.queryBuilder.getQuery(),
    });
    const hasCache = this.filter.cacheCtrl.has('suggestions', cacheContext);
    if (hasCache) {
      return this.filter.cacheCtrl.get('suggestions', cacheContext);
    }

    // check if attribute exists
    const attribute = this.model.getAttribute(options.attribute);
    if (!attribute) {
      throw new Error(`Attribute ${options.attribute} does not exist`);
    }

    if (typeof options.value === 'string' && options.value.startsWith('/') && options.value.endsWith('/')) {
      try {
        new RegExp(options.value.slice(1, -1));
      } catch (err) {
        return [];
      }
    }

    let relevantData: Map<string, ListType>;
    let relevantQuery: DcuplGlobalQueryOptions<any> | undefined = undefined;
    if (options.relevantData === 'all') {
      relevantData = this.filter.initialData;
      relevantQuery = { modelKey: this.model.key, queries: [] };
    } else if (options.relevantData === 'filtered') {
      relevantData = this.filter.filteredData;
      relevantQuery = this.filter.queryBuilder.getQuery();
    } else if (options.relevantData === 'excludeQuery' && options.excludeQuery) {
      const qb = new DcuplQueryBuilder();
      qb.init(this.filter.queryBuilder.getQuery());
      qb.applyQuery(this.filter.queryBuilder.getQuery());
      qb.removeQuery(options.excludeQuery);
      relevantQuery = qb.getQuery();
      const filteredDataWithExcludedQuery = this.dcuplApi['queryManager'].queryData(
        this.filter.initialData,
        relevantQuery,
        false,
        this.filter.indicesController['indexMap'],
      );
      relevantData = filteredDataWithExcludedQuery;
    } else {
      // default - use filtered data
      relevantData = this.filter.filteredData;
      relevantQuery = this.filter.queryBuilder.getQuery();
    }

    const indexMap = this.filter.indicesController.getOrCreateIndex(options.attribute, relevantData, {
      query: relevantQuery,
    });

    const suggestions = computeSuggestions(relevantData, indexMap, options, this.dcuplApi['queryManager']);

    this.filter.cacheCtrl.set('suggestions', cacheContext, suggestions);

    return suggestions;
  }

  @trigger({
    scope: 'list',
    type: 'fn_facets',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getFacets(options: DcuplFacetOptions): DcuplFacet[] {
    const cacheContext = this.filter.cacheCtrl.generateContext({
      options,
      query: this.filter.queryBuilder.getQuery(),
    });
    const hasCache = this.filter.cacheCtrl.has('facets', cacheContext);
    if (hasCache) {
      return this.filter.cacheCtrl.get('facets', cacheContext);
    }

    const reference = this.model.references.get(options.attribute);

    let remoteModel: DcuplModel<ListItem> | undefined;

    if (reference) {
      remoteModel = this.filter.modelParser.models.get(reference!.model);
    }

    if (typeof options.excludeZeros === 'undefined') {
      options.excludeZeros = true;
    }

    if (typeof options.excludeUndefineds === 'undefined') {
      options.excludeUndefineds = true;
    }

    if (typeof options.excludeUnresolved === 'undefined') {
      options.excludeUnresolved = true;
    }

    if (typeof options.calculateResults === 'undefined') {
      options.calculateResults = true;
    }

    const entries: DcuplFacet[] = this.filter.getFacets(options.attribute, remoteModel, {
      calculateFacets: options.calculateResults,
      count: options.count,
      excludeZeros: options.excludeZeros,
      excludeUndefineds: options.excludeUndefineds,
      excludeUnresolved: options.excludeUnresolved,
      skipProcessing: false,
    });

    this.filter.cacheCtrl.set('facets', cacheContext, entries);

    return entries;
  }

  @trigger({
    scope: 'list',
    type: 'fn_aggregate',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private getAggregate(options: AggregationOptions): Aggregation {
    const cacheContext = this.filter.cacheCtrl.generateContext({
      options,
      query: this.filter.queryBuilder.getQuery(),
    });
    const hasCache = this.filter.cacheCtrl.has('aggregation', cacheContext);
    if (hasCache) {
      return this.filter.cacheCtrl.get('aggregation', cacheContext);
    }

    const aggregate = this.filter.getAggregate(
      options,
      this.filter.filteredData,
      this.filter.indicesController.getOrCreateIndex(
        options.attribute,
        this.filter.filteredData,
        this.filter.queryBuilder.getQuery(),
      ),
    );
    this.filter.cacheCtrl.set('aggregation', cacheContext, aggregate);
    return aggregate;
  }

  @trigger({
    scope: 'list',
    type: 'fn_pivot',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getPivot(options: PivotOptions): PivotResponse {
    if (
      !options ||
      !Array.isArray(options.columns) ||
      !Array.isArray(options.rows) ||
      !options.values ||
      !options.values.length
    ) {
      console.log(`Invalid pivot options`);
      return { key: 'invalid_options' };
    }

    const cacheContext = this.filter.cacheCtrl.generateContext({
      options,
      query: this.filter.queryBuilder.getQuery(),
    });
    const hasCache = this.filter.cacheCtrl.has('pivot', cacheContext);
    if (hasCache) {
      return this.filter.cacheCtrl.get('pivot', cacheContext);
    }

    /**
     * TODO: Check for possible Improvement
     * We are passing a new indicesController every time we call pivot
     * We should probably pass a valid context depending on the current pivoting operation
     * After the last pivoting operation there are more fine tuned indices that can be reused
     * Check if those child indices (current query + current pivot depth) can be reused...
     */
    let indicesCtrl: IndicesController = new IndicesController();

    if (this.filter.filteredData.size === this.filter.initialData.size) {
      indicesCtrl = this.filter.indicesController;
    }

    const result = pivot(this.filter.filteredData, options, { key: 'root' }, 'root', indicesCtrl);

    this.filter.cacheCtrl.set('pivot', cacheContext, result);

    return result;
  }
}
